import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import ReactDOMClient from "react-dom/client";
import CompareCard from "./comparecard.jsx";

export default function CompareModal({ checkoutEnabled = true}) {
    const [productCodes, setProductCodes] = useState([]);
    const [products, setProducts] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        function handleCompareToggle(e) {
            const { code, checked } = e.detail;

            setProductCodes((prev) => {
                if (checked) {
                    if (prev.includes(code)) return prev;
                    if (prev.length >= 6) return prev;
                    return [...prev, code];
                } else {
                    return prev.filter((c) => c !== code);
                }
            });
        }

        window.addEventListener("compare-toggle", handleCompareToggle);
        return () =>
            window.removeEventListener("compare-toggle", handleCompareToggle);
    }, []);

    useEffect(() => {
        if (productCodes.length === 0) {
            setProducts([]);
            setLoading(false);
        }

        if (!isOpen || productCodes.length === 0) {
            return;
        }

        const controller = new AbortController();

        const fetchProducts = async () => {
            try {
                setLoading(true);

                const query = productCodes
                    .map((c) => `codes=${encodeURIComponent(c)}`)
                    .join("&");
                const res = await fetch(`/api/Catalog/products/compare?${query}`, {
                    signal: controller.signal,
                });

                if (!res.ok) throw new Error("Failed to fetch compare data");

                const data = await res.json();

                setProducts(data.products || []);
            } catch (err) {
                if (err.name !== "AbortError")
                    console.error("Compare fetch error:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchProducts();
        return () => controller.abort();
    }, [isOpen, productCodes]);

    const open = () => setIsOpen(true);
    
    const close = () => setIsOpen(false);
    
    const removeAll = () => {
        productCodes.forEach(code => {
            window.dispatchEvent(
                new CustomEvent("compare-toggle", {
                    detail: { code, img: "", checked: false },
                })
            );
        });
        setProductCodes([]);
    };

    useEffect(() => {
        window.compareModal = { open, close };
    }, []);

    useEffect(() => {
        document.body.style.overflow = isOpen ? "hidden" : "";
    }, [isOpen]);

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div className="compare-modal-overlay" onClick={close}>
            <div className="compare-modal" onClick={(e) => e.stopPropagation()}>
                <div className="compare-modal__header">
                    <div className="compare-modal__header-title">
                        <h2>Compare Styles</h2>
                        <p>{products.length} Selected</p>
                    </div>

                    <div className="compare-modal__header-actions compare-modal__header-actions-desktop">
                        <button className="btn-secondary" onClick={() => removeAll()}>
                            Remove All
                        </button>
                    </div>

                    <button
                        type="button"
                        className="compare-modal__close"
                        onClick={close}
                    >
                        <img src="/images/close-icon.svg" alt="Close modal" />
                    </button>
                </div>

                <div className="compare-modal__content">
                    {loading ? (
                        <p className="compare-modal__loading">Loading...</p>
                    ) : (
                        <div className="compare-modal__items">
                            {products.map((p) => (
                                <CompareCard
                                    key={p.styleCatalogCode}
                                    product={p}
                                    setProductCodes={setProductCodes}
                                    checkoutEnabled={checkoutEnabled}
                                />
                            ))}
                        </div>
                    )}
                </div>

                <div className="compare-modal__header-actions compare-modal__header-actions-mobile">
                    <button className="btn-secondary" onClick={() => removeAll()}>
                        Remove All
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}

export function initCompareModal() {
    const container = document.getElementById("compareModalRoot");
    if (!container) return;

    // pass the checkout-enabled flag down to the modal so the cards can use it.
    // defaults to true to keep existing functionality.
    const checkoutEnabledAttr = container.getAttribute("data-checkout-enabled");
    const checkoutEnabled = JSON.parse(checkoutEnabledAttr ?? "true");

    const root = ReactDOMClient.createRoot(container);
    root.render(<CompareModal checkoutEnabled={ checkoutEnabled }/>);
}
